/* In-app replacements for window.confirm / window.prompt / window.alert.
 *
 * Why: native dialogs are inconsistent inside WKWebView and some Android
 * WebView builds: they can appear unstyled, show the wrong origin, or (with
 * certain Capacitor configurations) not appear at all. Since delete and folder
 * naming both depend on them, they have to be real DOM.
 *
 * Same call shape as the built-ins, except they return promises:
 *     if (await confirmDialog("Delete this word?")) ...
 *     const name = await promptDialog("Folder name");   // null if cancelled
 */

let host;

function ensureHost() {
  if (host && document.body.contains(host)) return host;
  host = document.createElement("div");
  host.className = "modal-host";
  host.setAttribute("aria-live", "polite");
  document.body.appendChild(host);
  return host;
}

function open({ title, message, input, inputValue, confirmLabel, cancelLabel, danger }) {
  return new Promise((resolve) => {
    const root = ensureHost();
    const lastFocus = document.activeElement;

    const wrap = document.createElement("div");
    wrap.className = "modal-backdrop";
    wrap.innerHTML = `
      <div class="modal" role="dialog" aria-modal="true" aria-label="${title || "Confirm"}">
        ${title ? `<h4 class="modal-title"></h4>` : ""}
        ${message ? `<p class="modal-msg"></p>` : ""}
        ${input ? `<input type="text" class="modal-input" autocomplete="off">` : ""}
        <div class="modal-actions">
          ${cancelLabel === null ? "" : `<button class="go ghost modal-cancel"></button>`}
          <button class="go modal-ok${danger ? " danger-solid" : ""}"></button>
        </div>
      </div>`;

    /* textContent, never innerHTML: these strings can contain a user's own word. */
    if (title) wrap.querySelector(".modal-title").textContent = title;
    if (message) wrap.querySelector(".modal-msg").textContent = message;
    const okBtn = wrap.querySelector(".modal-ok");
    okBtn.textContent = confirmLabel || "OK";
    const cancelBtn = wrap.querySelector(".modal-cancel");
    if (cancelBtn) cancelBtn.textContent = cancelLabel || "Cancel";
    const field = wrap.querySelector(".modal-input");
    if (field && inputValue) field.value = inputValue;

    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey, true);
      wrap.remove();
      try { lastFocus?.focus?.(); } catch { /* ignore */ }
      resolve(value);
    };

    const accept = () => done(field ? field.value.trim() || null : true);
    const reject = () => done(field ? null : false);

    okBtn.onclick = accept;
    if (cancelBtn) cancelBtn.onclick = reject;
    wrap.onclick = (e) => { if (e.target === wrap) reject(); };

    function onKey(e) {
      if (e.key === "Escape") { e.preventDefault(); reject(); }
      else if (e.key === "Enter" && (field || document.activeElement === okBtn)) {
        e.preventDefault(); accept();
      } else if (e.key === "Tab") {
        /* keep focus inside the dialog */
        const f = [...wrap.querySelectorAll("button, input")].filter((n) => !n.disabled);
        if (!f.length) return;
        const i = f.indexOf(document.activeElement);
        const next = e.shiftKey ? (i <= 0 ? f.length - 1 : i - 1) : (i === f.length - 1 ? 0 : i + 1);
        e.preventDefault();
        f[next].focus();
      }
    }
    document.addEventListener("keydown", onKey, true);

    root.appendChild(wrap);
    (field || okBtn).focus();
  });
}

export const confirmDialog = (message, opts = {}) =>
  open({ message, confirmLabel: opts.confirmLabel || "OK", danger: opts.danger, ...opts });

export const promptDialog = (message, defaultValue = "", opts = {}) =>
  open({ message, input: true, inputValue: defaultValue, confirmLabel: opts.confirmLabel || "Save", ...opts });

export const alertDialog = (message, opts = {}) =>
  open({ message, cancelLabel: null, confirmLabel: opts.confirmLabel || "OK", ...opts });

/**
 * Several labelled choices plus Cancel. Resolves to the chosen `value`, or
 * null if cancelled. Used where a yes/no would force a hidden default.
 *     await choiceDialog("Delete folder?", [
 *       { label: "Keep the words", value: "keep" },
 *       { label: "Delete the words too", value: "delete", danger: true },
 *     ]);
 */
export function choiceDialog(message, choices, opts = {}) {
  return new Promise((resolve) => {
    const root = ensureHost();
    const lastFocus = document.activeElement;
    const wrap = document.createElement("div");
    wrap.className = "modal-backdrop";
    wrap.innerHTML = `
      <div class="modal" role="dialog" aria-modal="true" aria-label="${opts.title || "Choose"}">
        ${opts.title ? `<h4 class="modal-title"></h4>` : ""}
        <p class="modal-msg"></p>
        <div class="modal-choices"></div>
        <button class="go ghost modal-cancel" style="margin-top:6px"></button>
      </div>`;
    if (opts.title) wrap.querySelector(".modal-title").textContent = opts.title;
    wrap.querySelector(".modal-msg").textContent = message;
    const list = wrap.querySelector(".modal-choices");
    choices.forEach((c) => {
      const b = document.createElement("button");
      b.className = "go" + (c.danger ? " danger-solid" : " ghost");
      b.style.marginTop = "6px";
      b.textContent = c.label;
      b.onclick = () => done(c.value);
      list.appendChild(b);
    });
    const cancel = wrap.querySelector(".modal-cancel");
    cancel.textContent = opts.cancelLabel || "Cancel";
    cancel.onclick = () => done(null);
    wrap.onclick = (e) => { if (e.target === wrap) done(null); };

    let settled = false;
    function done(v) {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey, true);
      wrap.remove();
      try { lastFocus?.focus?.(); } catch { /* ignore */ }
      resolve(v);
    }
    function onKey(e) { if (e.key === "Escape") { e.preventDefault(); done(null); } }
    document.addEventListener("keydown", onKey, true);
    root.appendChild(wrap);
    list.querySelector("button")?.focus();
  });
}
